import styled, { css } from 'styled-components'
import WorkPlannerOperationCard from './WorkPlannerOperationCard'
import useTranslation from '../../hooks/useTranslation'
import Loader from '../generic/Loader'

const Area = styled.div`
    display: flex;
    flex-direction: column;
    border-bottom: 1px solid ${({ theme }) => theme.area.lightBorder};
    background: ${({ theme }) => theme.area.bg};
    flex-shrink: 0;
`

const WoStrip = styled.div`
    display: flex;
    align-items: center;
    gap: 4px;
    padding: 0.3rem 1.25rem;
    overflow-x: auto;
    border-bottom: 1px solid ${({ theme }) => theme.area.lightBorder};
    scrollbar-width: none;
    &::-webkit-scrollbar { display: none; }
`

const WoPill = styled.button`
    background: ${({ theme, $active }) => $active ? theme.btn.primary : 'transparent'};
    border: 1px solid ${({ theme, $active }) => $active ? theme.btn.primary : theme.area.lightBorder};
    border-radius: 0.3rem;
    color: ${({ theme, $active }) => $active ? theme.card.background : theme.font.color};
    cursor: pointer;
    font-size: var(--font-XS);
    font-weight: 500;
    padding: 2px 8px;
    white-space: nowrap;
    flex-shrink: 0;
    line-height: 1.4;
    transition: background 0.12s, border-color 0.12s, color 0.12s;

    &:hover {
        background: ${({ theme, $active }) => $active ? theme.btn.primary : theme.area.highBg};
        border-color: ${({ theme }) => theme.btn.primary};
        color: ${({ theme, $active }) => $active ? theme.card.background : theme.font.color};
    }
`

const CardsRow = styled.div`
    display: flex;
    align-items: center;
    gap: 0.75rem;
    padding: 0.75rem 1.25rem;
    overflow-x: auto;
    min-height: 88px;

    scrollbar-width: thin;
    scrollbar-color: ${({ theme }) => theme.area.highBg} transparent;
    &::-webkit-scrollbar { height: 5px; }
    &::-webkit-scrollbar-thumb { background: ${({ theme }) => theme.area.highBg}; border-radius: 3px; }
`

const Label = styled.span`
    font-size: var(--font-XS);
    font-weight: 600;
    color: ${({ theme }) => theme.font.label};
    white-space: nowrap;
    flex-shrink: 0;
`

const Divider = styled.div`
    width: 1px;
    height: 40px;
    background: ${({ theme }) => theme.area.lightBorder};
    flex-shrink: 0;
`

const EmptyLabel = styled.span`
    font-size: var(--font-S);
    color: ${({ theme }) => theme.font.label};
`

const WorkPlannerPickArea = ({
    pendingOps, scheduledOps, selectedOpId, onSelectOp, onUnschedule, loading,
    uniqueJobs, jobId, onSelectJob, fieldConfig, labelConfig
}) => {
    const { t } = useTranslation()

    if (loading) return <Area><CardsRow><Loader /></CardsRow></Area>

    const allOps = [...pendingOps, ...scheduledOps]

    return (
        <Area>
            {uniqueJobs?.length > 0 && (
                <WoStrip>
                    {uniqueJobs.map(job => (
                        <WoPill
                            key={job.id}
                            $active={job.id === jobId}
                            onClick={() => onSelectJob(job)}
                        >
                            {job.display ?? job.id}
                        </WoPill>
                    ))}
                </WoStrip>
            )}

            <CardsRow>
                {allOps.length === 0 && (
                    <EmptyLabel>{t(labelConfig?.pickNoOps ?? 'manufacturing.planner.pick.noOps')}</EmptyLabel>
                )}

                {pendingOps.length > 0 && (
                    <Label>{t(labelConfig?.pickPending ?? 'manufacturing.planner.pick.pending')}</Label>
                )}

                {pendingOps.map(op => (
                    <WorkPlannerOperationCard
                        key={op.id}
                        op={op}
                        selected={op.id === selectedOpId}
                        onSelect={onSelectOp}
                        fieldConfig={fieldConfig}
                    />
                ))}

                {scheduledOps.length > 0 && pendingOps.length > 0 && <Divider />}

                {scheduledOps.length > 0 && (
                    <Label>{t(labelConfig?.pickScheduled ?? 'manufacturing.planner.pick.scheduled')}</Label>
                )}

                {scheduledOps.map(op => (
                    <WorkPlannerOperationCard
                        key={op.id}
                        op={op}
                        selected={op.id === selectedOpId}
                        onSelect={onSelectOp}
                        onUnschedule={onUnschedule}
                        fieldConfig={fieldConfig}
                    />
                ))}
            </CardsRow>
        </Area>
    )
}

export default WorkPlannerPickArea
